import { useCallback, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  useArcade,
  useDeleteDrawing,
  useGallery,
  useSaveDrawing,
  useUnlockPixelPaint,
} from '../api/hooks'
import { Button, Card, ErrorText, Input, Spinner } from '../components/ui'
import {
  GRID,
  PALETTE,
  blankCanvas,
  decode,
  encode,
  floodFill,
  toDataUri,
} from '../lib/pixels'
import { sfx } from '../lib/sound'

type Tool = 'draw' | 'fill' | 'erase'

export default function PixelPaint() {
  const { data: arcade, isPending } = useArcade()
  const unlock = useUnlockPixelPaint()

  if (isPending) return <Spinner />

  if (!arcade?.pixel_paint_unlocked) {
    return (
      <div className="mx-auto max-w-md space-y-4 text-center">
        <div className="text-6xl">🎨</div>
        <h1 className="text-2xl font-semibold tracking-tight">Pixel paint</h1>
        <Card className="space-y-4">
          <p className="text-sm text-muted">
            A 32×32 canvas, sixteen colours, and nowhere to be. Unlock it once
            and it stays yours — no token per go, because there is nothing to
            win and nothing to lose.
          </p>
          <p className="text-sm text-muted">
            Anything you make can become a language's icon.
          </p>
          <ErrorText error={unlock.error} />
          <Button
            onClick={() => unlock.mutate()}
            disabled={unlock.isPending}
            className="w-full"
          >
            Unlock for {arcade?.pixel_paint_unlock_cost ?? 50} tokens
          </Button>
          <p className="text-xs text-muted">You have {arcade?.balance ?? 0}.</p>
        </Card>
        <Link to="/arcade">
          <Button variant="secondary">Back to arcade</Button>
        </Link>
      </div>
    )
  }

  return <Canvas />
}

function Canvas() {
  const [pixels, setPixels] = useState<number[]>(blankCanvas)
  const [color, setColor] = useState(1)
  const [tool, setTool] = useState<Tool>('draw')
  const [name, setName] = useState('')
  const [history, setHistory] = useState<number[][]>([])
  const painting = useRef(false)

  const save = useSaveDrawing()
  const { data: gallery } = useGallery()
  const remove = useDeleteDrawing()

  const pushHistory = useCallback((current: number[]) => {
    // Bounded: keeping every stroke of a long session would grow without limit.
    setHistory((h) => [...h.slice(-29), current])
  }, [])

  const paint = useCallback(
    (index: number, isStart: boolean) => {
      setPixels((current) => {
        if (isStart) pushHistory(current)

        if (tool === 'fill') {
          return floodFill(current, GRID, index, color)
        }
        const value = tool === 'erase' ? 0 : color
        if (current[index] === value) return current
        const next = [...current]
        next[index] = value
        return next
      })
    },
    [color, tool, pushHistory],
  )

  function undo() {
    setHistory((h) => {
      if (h.length === 0) return h
      setPixels(h[h.length - 1])
      return h.slice(0, -1)
    })
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="flex items-center justify-between text-sm">
        <Link to="/arcade" className="text-muted hover:text-ink">
          ← Arcade
        </Link>
      </div>

      <h1 className="text-2xl font-semibold tracking-tight">Pixel paint</h1>

      <div className="grid gap-5 md:grid-cols-[1fr_auto]">
        {/* The canvas. Pointer events rather than click, so dragging draws. */}
        <div
          className="grid aspect-square w-full select-none overflow-hidden rounded-xl
            border border-border-subtle bg-surface"
          style={{
            gridTemplateColumns: `repeat(${GRID}, minmax(0, 1fr))`,
            // A checkerboard shows through transparent cells.
            backgroundImage:
              'repeating-conic-gradient(var(--color-canvas) 0% 25%, transparent 0% 50%)',
            backgroundSize: '16px 16px',
          }}
          onPointerDown={() => {
            painting.current = true
          }}
          onPointerUp={() => {
            painting.current = false
          }}
          onPointerLeave={() => {
            painting.current = false
          }}
        >
          {pixels.map((value, index) => (
            <div
              key={index}
              onPointerDown={() => paint(index, true)}
              onPointerEnter={() => {
                if (painting.current && tool !== 'fill') paint(index, false)
              }}
              className="aspect-square"
              style={{
                background: value === 0 ? 'transparent' : PALETTE[value],
                touchAction: 'none',
              }}
            />
          ))}
        </div>

        {/* Tools. */}
        <div className="space-y-4 md:w-48">
          <div className="grid grid-cols-8 gap-1 md:grid-cols-4">
            {PALETTE.map((swatch, index) => (
              <button
                key={index}
                onClick={() => {
                  setColor(index)
                  setTool('draw')
                }}
                aria-label={`Colour ${index}`}
                className={`aspect-square rounded-md border-2 ${
                  color === index && tool === 'draw'
                    ? 'border-brand'
                    : 'border-border-subtle'
                }`}
                style={{
                  background: index === 0 ? 'var(--color-canvas)' : swatch,
                }}
              >
                {index === 0 && <span className="text-xs text-muted">∅</span>}
              </button>
            ))}
          </div>

          <div className="flex gap-1">
            {(['draw', 'fill', 'erase'] as Tool[]).map((option) => (
              <Button
                key={option}
                variant={tool === option ? 'primary' : 'secondary'}
                onClick={() => setTool(option)}
                className="flex-1 px-2"
              >
                {option === 'draw' ? '✏️' : option === 'fill' ? '🪣' : '🧽'}
              </Button>
            ))}
          </div>

          <div className="flex gap-1">
            <Button
              variant="secondary"
              onClick={undo}
              disabled={history.length === 0}
              className="flex-1"
            >
              Undo
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                pushHistory(pixels)
                setPixels(blankCanvas())
              }}
            >
              Clear
            </Button>
          </div>

          <div className="space-y-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name it"
              maxLength={60}
            />
            <Button
              className="w-full"
              disabled={save.isPending}
              onClick={() =>
                save.mutate(
                  {
                    name: name.trim() || 'Untitled',
                    width: GRID,
                    height: GRID,
                    pixels: encode(pixels),
                  },
                  {
                    onSuccess: () => {
                      sfx.complete()
                      setName('')
                    },
                  },
                )
              }
            >
              {save.isPending ? 'Saving…' : 'Save to gallery'}
            </Button>
            <ErrorText error={save.error} />
          </div>
        </div>
      </div>

      {gallery && gallery.length > 0 && (
        <div className="space-y-2">
          <h2 className="font-medium">Your gallery</h2>
          <p className="text-sm text-muted">
            Set any of these as a language's icon from its page.
          </p>
          <ul className="grid grid-cols-3 gap-3 sm:grid-cols-5">
            {gallery.map((art) => (
              <li key={art.id}>
                <Card className="space-y-2 p-2">
                  <img
                    src={toDataUri(decode(art.pixels, art.width * art.height), art.width)}
                    alt={art.name}
                    className="w-full rounded-md bg-canvas [image-rendering:pixelated]"
                  />
                  <p className="truncate text-xs">{art.name}</p>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      className="px-2 py-1 text-xs"
                      onClick={() =>
                        setPixels(decode(art.pixels, art.width * art.height))
                      }
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      className="px-2 py-1 text-xs"
                      onClick={() => remove.mutate(art.id)}
                    >
                      ✕
                    </Button>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
